import React from 'react';
import {
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Tooltip,
} from '@mui/material';
import ExcelJS from "exceljs";
import { useSelector } from 'react-redux';

import { dropdownValidation, positiveIntegerValidation, TYPE_DROPDOWN_VALUES, STATUS_DROPDOWN_VALUES, PRIORITY_DROPDOWN_VALUES, TOTAL_ROWS, FORMULA_ROWS, hexToARGB, max200CharValidation } from "../../utils/excelExportHelperFunction";
import { randomAlphaNumeric } from '../../utils/commonfunctions';


const ErrorModal = ({ open, errors, onClose, totalTestCases=0, createdTestCases=0, failedTestCases=0, invalidTestCases=0,rowsInvolved,modulePaths,labelOptions }) => {
  const { selectedProject } = useSelector((state) => state.projects);
  const createdPercentage = (createdTestCases / totalTestCases) * 100;
  const failedPercentage = (failedTestCases / totalTestCases) * 100;
  const invalidPercentage = (invalidTestCases / totalTestCases) * 100;

  const handleExcelSheetDownloadWithData = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Data");

      const headers = [
        "Module", "title", "description", "type",
        "status", "priority", "estimated_duration", "labels", "testCaseId"
      ];
      worksheet.addRow(headers);

      // Fill rows with actual data from rowsInvolved
      rowsInvolved.forEach((rowObj) => {
        const { testCase, testCaseId, error } = rowObj;
        const moduleName = Object.keys(modulePaths).find(
          (key) => modulePaths[key] === testCase.Module
        ) || testCase.Module;

        const newRow = worksheet.addRow([
          moduleName || "",
          testCase.title || "",
          testCase.description || "",
          testCase.type || "",
          testCase.status || "",
          testCase.priority || "",
          testCase.estimated_duration || "",
          Array.isArray(testCase.labels)
            ? testCase.labels.map(l => l.name || l).join(", ")
            : "",
          testCaseId || ""
        ]);

        if (!error) {
          newRow.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb:"C6EFCE" }, 
            };
            cell.font = { color: { argb: "006100" } };
          });
        }

      });

      const filledRows = rowsInvolved.length;
      for (let i = filledRows + 1; i <= TOTAL_ROWS; i++) {
        worksheet.addRow(new Array(headers.length).fill(""));
      }
      // Add dropdown hidden sheets just like in handleSheetDownload
      const dropdownSheet = workbook.addWorksheet("DropdownOptions");
      const moduleKeys = Object.keys(modulePaths);
      moduleKeys.forEach((key, idx) => {
        dropdownSheet.getCell(`A${idx + 1}`).value = key;
        dropdownSheet.getCell(`B${idx + 1}`).value = modulePaths[key];
      });
      dropdownSheet.state = "veryHidden";

      const labelSheet = workbook.addWorksheet("LabelOptions");

      if (labelOptions && labelOptions.length > 0) {
        labelOptions.forEach((label, idx) => {
          labelSheet.getCell(`A${idx + 1}`).value = label.name;
          labelSheet.getCell(`B${idx + 1}`).value = label.id;
        });
      } else {
        labelSheet.getCell("A1").value = "No Labels";
        labelSheet.getCell("B1").value = "";
      }

      labelSheet.state = "veryHidden";

      // Resize columns dynamically
      worksheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const value = cell.value ? cell.value.toString() : "";
          maxLength = Math.max(maxLength, value.length);
        });
        column.width = maxLength < 10 ? 10 : maxLength + 2;
      });


      const metaSheet = workbook.addWorksheet("MetaInfo");
      metaSheet.getCell("A1").value = "Generated by";
      metaSheet.getCell("B1").value = "AP-TEST-CASE-IMPORT";
      metaSheet.getCell("A2").value = "Project ID";
      metaSheet.getCell("B2").value = selectedProject?.id || null;
      metaSheet.state = "veryHidden";

      // Add notes to headers

      const moduleRange = `DropdownOptions!$A$1:$A$${moduleKeys.length}`;
      const totalLabelRows = (labelOptions && labelOptions.length > 0) ? labelOptions.length : 1;
      const labelRange = `LabelOptions!$A$1:$A$${totalLabelRows}`;
      for (let row = 2; row < 2 + FORMULA_ROWS; row++) {
        worksheet.getCell(`A${row}`).dataValidation = dropdownValidation([moduleRange]);
        worksheet.getCell(`B${row}`).dataValidation = max200CharValidation;
        worksheet.getCell(`D${row}`).dataValidation = dropdownValidation(TYPE_DROPDOWN_VALUES);
        worksheet.getCell(`E${row}`).dataValidation = dropdownValidation(STATUS_DROPDOWN_VALUES);
        worksheet.getCell(`F${row}`).dataValidation = dropdownValidation(PRIORITY_DROPDOWN_VALUES);
        worksheet.getCell(`G${row}`).dataValidation = positiveIntegerValidation;
        worksheet.getCell(`H${row}`).dataValidation = dropdownValidation([labelRange], false);
      }
      worksheet.getCell("A1").note =
        "Module path or ID. If dropdown selected, mapped ID will be used.";
      worksheet.getCell("H1").note =
        "Labels (comma-separated if multiple).";
      worksheet.getCell("I1").note =
        "Generated testCaseId (if created). Empty if failed/invalid.";

      // Export to browser
      const password = randomAlphaNumeric(56);
      await worksheet.protect(password, {
        selectLockedCells: true,
        selectUnlockedCells: true,
        formatCells: false,
        formatColumns: true,
        formatRows: false,
        insertColumns: false,
        insertRows: false,
        insertHyperlinks: false,
        deleteColumns: false,
        deleteRows: false,
        sort: false,
        autoFilter: false,
        pivotTables: false,
      });

      // Unlock all cells
      worksheet.eachRow((row, rowNumber) => {
        row.eachCell(cell => {
          cell.protection = { locked: false };
        });
      });

      // Re-lock only the first row (headers)
      worksheet.getRow(1).eachCell(cell => {
        cell.protection = { locked: true };
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "TestCaseImportResult.xlsx";
      link.click();
    } catch (error) {
      console.error("Error exporting Excel with data:", error);
    }
  };
 

  return (
    <Modal
      open={open}
    //   aria-labelledby="error-modal-title"
    //   aria-describedby="error-modal-description"
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
    >
      <Box className="modal-container"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <Typography variant="h6">Import Test Case Summary</Typography>

        {/* Status Bar */}
        {totalTestCases > 0 && (

        <Box sx={{ width: '100%', mt: 2, mb: 4, position: 'relative', height: 30 }}>
          {/* Background bar */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: '#e0e0e0',
              borderRadius: 5,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Created section */}
            { createdTestCases>0 && (
                <Tooltip title={`Valid: ${createdTestCases} (${createdPercentage.toFixed(2)}%)`} arrow>
                <Box
                    sx={{
                    width: `${createdPercentage}%`,
                    height: '100%',
                    backgroundColor: '#4caf50',
                    borderRadius: '5px 0 0 5px',
                    position: 'absolute',
                    left: '0%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    }}
                >
                    <Typography
                    variant="body2"
                    sx={{
                        color: 'white',
                        // textAlign: 'center',
                        // lineHeight: '30px',
                        // position: 'relative',
                    }}
                    >
                    {createdTestCases}
                    </Typography>
                </Box>
                </Tooltip>
            )}
            {/* Failed section */}
            { failedTestCases>0 && (
                <Tooltip title={`Failed: ${failedTestCases} (${failedPercentage.toFixed(2)}%)`} arrow>
                  <Box
                    sx={{
                    width: `${failedPercentage}%`,
                    height: '100%',
                    backgroundColor: '#f44336', 
                    position: 'absolute',
                    left: `${createdPercentage}%`, // Start after "Created"
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'white',
                        // textAlign: 'center',
                        // lineHeight: '30px',
                        // position: 'relative',
                        // left: `${createdPercentage}%`, // Start after "Created"

                      }}
                    >
                      {failedTestCases}
                    </Typography>
                  </Box>
                </Tooltip>
            )}
              {/* Invalid section */}
              {invalidTestCases > 0 && (
                <Tooltip title={`Invalid: ${invalidTestCases} (${invalidPercentage.toFixed(2)}%)`} arrow>
                  <Box
                    sx={{
                      width: `${invalidPercentage}%`,
                      height: '100%',
                      backgroundColor: '#9e9e9e', // Grey for invalid
                      position: 'absolute',
                      left: `${createdPercentage + failedPercentage}%`,
                      top: '0', // Align at top
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',

                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'white',
                        // textAlign: 'center',
                        // lineHeight: '30px',
                        // position: 'relative',
                        // left: `${createdPercentage + failedPercentage}%`,
                      }}
                      >
                       {invalidTestCases}
                      </Typography>
                    </Box>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}

    {/* Total Test Cases Display */}
    <Box>
      <Typography variant="body1" color="textSecondary">
Download the Excel sheet to view detailed results, including errors. You can update the sheet and re-upload it to fix the issues.      </Typography>
    </Box>
    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={1}>
      <Typography variant="body1" color="textSecondary">
        Total Test Cases: <b>{totalTestCases}</b>
      </Typography>
     <Button
      variant="true"
      color="primary"
      size='small'
      onClick={handleExcelSheetDownloadWithData}
      >
      Export Excel
    </Button>
    </Box>

        {/* Error Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 270, overflowY: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                {/* <TableCell>Module Path</TableCell>
                <TableCell>Test Case</TableCell>
                <TableCell>Error Message</TableCell> */}
                <TableCell>Row Number</TableCell>
                <TableCell>Error Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {errors.map((error, index) => (
                <TableRow key={index}>
                  {/* <TableCell>
                    {error.module}
                    {error.component ? ` > ${error.component}` : ''}
                    {error.component ? (error.subcomponent ? ` > ${error.subcomponent}` : ''): ''}
                  </TableCell>
                  <TableCell>{error.testcase}</TableCell>
                  <TableCell>{error.error}</TableCell> */}
                  <TableCell>{error.row}</TableCell>
                  <TableCell>
                    {error.errors.map((err, idx) => (
                      <span key={idx}>
                        <strong>{err.column}:</strong> {err.message}{idx < error.errors.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ textAlign: 'right', mt: 2 }}>
          <Button variant="true" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ErrorModal;